/**
 * Dynamic-block per-INSERT parameter population (DYN-1.6 / closes
 * TODO(dynamic-block-params) left at `dxfBridge.dynamic.detect.ts:214-216`).
 *
 * For every BlockDefinition flagged as dynamic, derive a
 * `ParamDef[]` list (name / type / default / choices) and attach the
 * authored default values to each INSERT's `customProperties`. The
 * values populate AutoCAD's Properties palette list ("Длина=1200",
 * "Состояние видимости=hidden pipe", "Flipped=false", ...), which the
 * evaluator (`applyInstanceParameters`) and the UI
 * (`dxfGeometry.buildBlockInsertPreview`) both already consume via the
 * flat `Record<string, string | number | boolean>` shape of
 * `BlockInsertEntity.customProperties`.
 *
 * ── Scope ──
 *
 * Covers the five parameter kinds that AutoCAD's Properties palette
 * exposes for dynamic blocks most often, per the АБИ1.dxf sample
 * (`C:/Users/cosc/AppData/Local/Temp/abi1.dxf`):
 *
 *   - Linear / distance    (BLOCKLINEARPARAMETER / ACDBBLOCKLINEARPARAMETER)
 *   - Rotation / angle     (BLOCKROTATIONPARAMETER / ACDBBLOCKROTATIONPARAMETER)
 *   - Flip                 (BLOCKFLIPPARAMETER / ACDBBLOCKFLIPPARAMETER)
 *   - Visibility (state)   (BLOCKVISIBILITYPARAMETER / ACDBBLOCKVISIBILITYPARAMETER)
 *   - Lookup               (BLOCKLOOKUPPARAMETER / ACDBBLOCKLOOKUPPARAMETER)
 *
 * XY / Point / Alignment parameters and the ACTION graph (Stretch,
 * Rotate, Flip-Action, …) are out of scope for this pass — the action
 * graph drives the INSERT's rendering but carries no user-editable
 * value on the Properties palette. Expansion to XY / Point lands under
 * a follow-up TODO(complex-params) marker.
 *
 * ── Class-name prefix handling ──
 *
 * AutoCAD emits the eval-graph classes WITHOUT the leading `ACDB` prefix
 * (`BLOCKLINEARPARAMETER` rather than `ACDBBLOCKLINEARPARAMETER`), which
 * is the opposite of ezdxf-formatted synthetic DXFs used by the DYN-1
 * unit tests. The scanner accepts both spellings so real AutoCAD output
 * and round-tripped test fixtures both parse.
 *
 * ── Two-stage lookup ──
 *
 * Stage A — `extractDynamicBlockParams` walks the OBJECTS section and
 * produces a `ParamDef[]` keyed by block-master name. It first consults
 * `block.dynamicMetadata.parameters` (populated by the existing DYN-1
 * parser for ezdxf-style ACDB-prefixed synthetic inputs). For real
 * AutoCAD output — which uses the un-prefixed class names — it falls
 * back to a raw-groups scan of OBJECTS' `rawObjectGroups`, walking the
 * ACAD_EVALUATION_GRAPH → BLOCK*PARAMETER children chain.
 *
 * Stage B — `attachInsertCustomProperties` walks the DrawingEntity[]
 * tree (all spaces) and, for each Block INSERT, resolves the underlying
 * dynamic master (anonymous `*U<N>` variants map back via
 * `AcDbBlockRepBTag` XData on the variant BLOCK_RECORD, or via
 * `AcDbDynamicBlockTrueName` / `AcDbDynamicBlockTrueName2`) and seeds
 * `customProperties` with the master's authored defaults. Per-INSERT
 * overrides decoded from ACAD_ENHANCEDBLOCKDATA's binary XRECORDs are
 * out of scope — AutoCAD stores them in an opaque binary format whose
 * decoding is a separate project.
 */
import type {
  DxfDocument,
  DxfDynamicBlockObject,
  DxfGroup,
  DxfObjectsSection,
  DxfTablesSection,
  DxfXDataEntry,
} from "@dxf-ai-editor/dxf-io";
import { getObjectsSection, getTablesSection } from "@dxf-ai-editor/dxf-io";

import type { BlockDefinition, DrawingEntity } from "./model";
import {
  canonicalDynamicBlockClass,
  collectGroups,
  firstGroup,
  firstInt,
  firstNumber,
} from "./dxfBridge.dynamic.shared";

// ═══════════════════════════════════════════════════════════════════════════
// Public types
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Internal shape for a single dynamic-block parameter. `choices` is
 * populated for visibility / lookup so the UI can render the state as a
 * dropdown. Defaults are coerced to the same primitive types that
 * `BlockInsertEntity.customProperties` stores (string | number | boolean)
 * so stage B can attach them verbatim.
 *
 * DYN-4 Phase 3 extends the type set:
 *   - `"area"` — readonly scalar (m², cm², …) driven by a BPT output
 *     column. Displayed on the Properties palette but not editable.
 *   - `readonly` — `true` when the value is driven by a
 *     BlockPropertiesTable (either the row key picks it, or an Updated*
 *     column overrides it). The Inspector renders these as read-only.
 */
export interface ParamDef {
  readonly name: string;
  readonly type:
    | "distance"
    | "angle"
    | "visibility"
    | "lookup"
    | "flip"
    | "area";
  readonly defaultValue: string | number | boolean;
  readonly choices?: readonly string[];
  readonly readonly?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Un-prefixed class-name predicates
// ═══════════════════════════════════════════════════════════════════════════

// Accept both spellings — AutoCAD emits the un-prefixed form on every
// version we have tested (2018+), ezdxf emits the prefixed form.
// `canonicalDynamicBlockClass` normalises BLOCK* → ACDBBLOCK* so each
// predicate only needs to compare against the canonical spelling.
function isLinearParameter(type: string): boolean {
  return canonicalDynamicBlockClass(type) === "ACDBBLOCKLINEARPARAMETER";
}
function isRotationParameter(type: string): boolean {
  return canonicalDynamicBlockClass(type) === "ACDBBLOCKROTATIONPARAMETER";
}
function isFlipParameter(type: string): boolean {
  return canonicalDynamicBlockClass(type) === "ACDBBLOCKFLIPPARAMETER";
}
export function isVisibilityParameter(type: string): boolean {
  return canonicalDynamicBlockClass(type) === "ACDBBLOCKVISIBILITYPARAMETER";
}
function isLookupParameter(type: string): boolean {
  return canonicalDynamicBlockClass(type) === "ACDBBLOCKLOOKUPPARAMETER";
}
export function isVisibilityState(type: string): boolean {
  return canonicalDynamicBlockClass(type) === "ACDBBLOCKVISIBILITYSTATE";
}
function isPropertiesTable(type: string): boolean {
  return canonicalDynamicBlockClass(type) === "ACDBBLOCKPROPERTIESTABLE";
}

// ═══════════════════════════════════════════════════════════════════════════
// Raw-groups scanner
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Logical record extracted from the un-prefixed raw-groups of the
 * OBJECTS section. Each record corresponds to a single group-0 header
 * in `rawObjectGroups[]` with its subsequent groups accumulated until
 * the next header. Used by both this module (for param extraction) and
 * `dxfBridge.dynamic.visibility.ts` (for typed visibility metadata).
 */
export interface RawRecord {
  type: string;
  handle: string;
  ownerHandle: string;
  groups: DxfGroup[];
}

/**
 * Split `rawObjectGroups` into logical records (one per group-0 header)
 * and pull handle + ownerHandle for every record. The split mirrors
 * `parseObjectsSection` but we cannot just use the section's parsed
 * buckets because the dynamic-block authoring classes land in
 * `rawObjectGroups` (their un-prefixed `BLOCK*` spellings fail
 * `isSupportedDynamicBlockType`).
 *
 * Exported for reuse in `dxfBridge.dynamic.visibility.ts` — promoting
 * from `__testables` keeps logic in one place and honours the
 * "split imports" rule (re-export ≠ local import).
 */
export function splitRawRecords(
  rawObjectGroups: readonly DxfGroup[],
): RawRecord[] {
  const records: RawRecord[] = [];
  let current: RawRecord | null = null;
  for (const g of rawObjectGroups) {
    if (g.code === 0) {
      if (current) records.push(current);
      current = {
        type: g.value.trim(),
        handle: "",
        ownerHandle: "",
        groups: [g],
      };
      continue;
    }
    if (!current) continue;
    current.groups.push(g);
    if (current.handle === "" && g.code === 5) current.handle = g.value.trim();
    if (current.ownerHandle === "" && g.code === 330)
      current.ownerHandle = g.value.trim();
  }
  if (current) records.push(current);
  return records;
}

// ═══════════════════════════════════════════════════════════════════════════
// Parameter decoders (shared between ACDB-prefixed and un-prefixed forms)
// ═══════════════════════════════════════════════════════════════════════════

function paramName(groups: readonly DxfGroup[], handle: string): string {
  // DXF group 300 — AcDbBlockElement's "name" field (the parameter's
  // display name in the Properties palette). AutoCAD pads the value
  // with stray whitespace in some versions; trim defensively.
  return firstGroup(groups as DxfGroup[], 300)?.trim() ?? `param_${handle}`;
}

function decodeLinear(rec: RawRecord): ParamDef {
  const groups = rec.groups;
  const name = paramName(groups, rec.handle);
  // Group 140 = authored distance under AcDbBlockLinearParameter.
  // Group 141 in the same subclass is unrelated on real AutoCAD output
  // (it belongs to the value-set substructure and is frequently 0 even
  // when 140 carries the displayed value). We prefer 140 outright —
  // ezdxf's synthetic emissions and real AutoCAD both write it. Fall
  // back to 141 only when 140 is absent (a safety net for unusual
  // encoders).
  const value = firstNumber(groups, 140) ?? firstNumber(groups, 141) ?? 0;
  return {
    name,
    type: "distance",
    defaultValue: Math.abs(value), // sign is direction-of-growth, not displayed
  };
}

function decodeRotation(rec: RawRecord): ParamDef {
  const groups = rec.groups;
  const name = paramName(groups, rec.handle);
  // Group 140 = authored angle (radians) under
  // AcDbBlockRotationParameter; 141 in real AutoCAD output is usually 0
  // (same value-set quirk as Linear). Prefer 140; AutoCAD stores
  // radians on-disk and converts to degrees for the Properties palette.
  const raw = firstNumber(groups, 140) ?? firstNumber(groups, 141) ?? 0;
  const degrees = (raw * 180) / Math.PI;
  return {
    name,
    type: "angle",
    defaultValue: Number.isFinite(degrees) ? roundAngle(degrees) : 0,
  };
}

function roundAngle(deg: number): number {
  // Keep 6 decimals — enough for all practical blocks without carrying
  // float noise into the Properties palette.
  const rounded = Math.round(deg * 1e6) / 1e6;
  return rounded === 0 ? 0 : rounded; // normalise -0
}

// ═══════════════════════════════════════════════════════════════════════════
// DYN-4 Phase 3 Fix 6 — readonly detection for constraint parameters
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Returns `true` when the given constraint parameter is driven by an
 * output column in a structured BlockPropertiesTable. AutoCAD marks these
 * columns with `kind: "updated-distance"` or `"updated-angle"` and stores
 * the constraint's OBJECTS handle in the column's `linkHandle`.
 *
 * The caller is responsible for passing the correct column kind
 * (distance constraints pass `"updated-distance"`; angular constraints
 * pass `"updated-angle"`). No fallback — a mismatch means the constraint
 * stays editable.
 */
function isConstraintDrivenByTable(
  constraintHandle: string,
  table: {
    columns?: ReadonlyArray<{ kind: string; linkHandle: string }> | undefined;
  },
  columnKind: "updated-distance" | "updated-angle",
): boolean {
  const columns = table.columns ?? [];
  for (const col of columns) {
    if (col.kind === columnKind && col.linkHandle === constraintHandle) {
      return true;
    }
  }
  return false;
}

function decodeFlip(rec: RawRecord): ParamDef {
  const groups = rec.groups;
  const name = paramName(groups, rec.handle);
  // AcDbBlockFlipParameter / AcDbBlock2PtParameter — group 280 is the
  // authored default value (0 = not flipped, 1 = flipped), group 281
  // is the current (evaluated) value.
  const raw = firstInt(groups, 281) ?? firstInt(groups, 280) ?? 0;
  return {
    name,
    type: "flip",
    defaultValue: raw !== 0,
  };
}

export function decodeVisibility(
  rec: RawRecord,
  statesByOwner: ReadonlyMap<string, { handle: string; name: string }[]>,
): ParamDef {
  const groups = rec.groups;
  const name = paramName(groups, rec.handle);

  // AutoCAD uses one of two encodings:
  //
  //   A. Inline state names via group 303 on the
  //      AcDbBlockVisibilityParameter subclass itself (AutoCAD 2018+
  //      output — confirmed on АБИ1.dxf). Each 303 is one state name;
  //      the order matches the encoded state order.
  //
  //   B. Child BLOCKVISIBILITYSTATE / ACDBBLOCKVISIBILITYSTATE records
  //      whose ownerHandle points back at the parameter, with the
  //      state name in group 2 or 300 (ezdxf / synthetic DXFs, plus
  //      the existing DYN-1 parser's expectation).
  //
  // Prefer (A) when any 303 is present because AutoCAD's own files
  // emit both the inline form AND any child records as round-trip
  // residue; (A) is authoritative in that case.
  const inline = collectGroups(groups, 303)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  let choices: string[];
  if (inline.length > 0) {
    // Deduplicate while preserving authoring order.
    const seen = new Set<string>();
    choices = [];
    for (const n of inline) {
      if (!seen.has(n)) {
        seen.add(n);
        choices.push(n);
      }
    }
  } else {
    const states = statesByOwner.get(rec.handle) ?? [];
    choices = states.map((s) => s.name);
  }

  // Pick the current state. Encoding (B) exposes group 340/341 as
  // state-handle references; (A) has no such handle — AutoCAD keeps
  // the current state ONLY in the binary AppDataCache XRECORD. In
  // that case we fall back to the first authored state, which mirrors
  // what Properties palette shows when the file opens cold.
  let defaultValue: string = choices[0] ?? "";
  const currentHandle = firstGroup(groups, 341)?.trim() ?? "";
  const defaultHandle = firstGroup(groups, 340)?.trim() ?? "";
  if (currentHandle || defaultHandle) {
    const states = statesByOwner.get(rec.handle) ?? [];
    const pickByHandle = (h: string): string | undefined =>
      states.find((s) => s.handle === h)?.name;
    defaultValue =
      pickByHandle(currentHandle) ??
      pickByHandle(defaultHandle) ??
      defaultValue;
  }

  const def: ParamDef = { name, type: "visibility", defaultValue };
  if (choices.length > 0) return { ...def, choices };
  return def;
}

function decodeLookup(
  rec: RawRecord,
  tablesByHandle: ReadonlyMap<string, { choices: string[]; current: string }>,
  tablesByEvalGraph: ReadonlyMap<string, { choices: string[]; current: string }>,
): ParamDef {
  const groups = rec.groups;
  const name = paramName(groups, rec.handle);
  // Resolution strategy:
  //   1. ezdxf-style DXFs put the BLOCKPROPERTIESTABLE handle in a
  //      group 340 reference on the parameter. Try that first.
  //   2. Real AutoCAD output does NOT emit such a reference — the
  //      lookup parameter and the properties table are siblings under
  //      the same ACAD_EVALUATION_GRAPH, linked via integer node ids
  //      inside the graph. A pragmatic fallback: pick the
  //      BLOCKPROPERTIESTABLE whose owner is the same eval-graph as
  //      this parameter. One table per block is the common case
  //      (confirmed on АБИ1.dxf).
  const tableHandle = firstGroup(groups, 340)?.trim() ?? "";
  const table =
    tablesByHandle.get(tableHandle) ?? tablesByEvalGraph.get(rec.ownerHandle);
  const choices = table?.choices ?? [];
  const defaultValue = table?.current ?? choices[0] ?? "";
  const def: ParamDef = { name, type: "lookup", defaultValue };
  if (choices.length > 0) return { ...def, choices };
  return def;
}

// ═══════════════════════════════════════════════════════════════════════════
// Visibility-state + properties-table helpers
// ═══════════════════════════════════════════════════════════════════════════

export function collectVisibilityStates(
  records: readonly RawRecord[],
): Map<string, { handle: string; name: string }[]> {
  const byOwner = new Map<string, { handle: string; name: string }[]>();
  for (const rec of records) {
    if (!isVisibilityState(rec.type)) continue;
    const owner = rec.ownerHandle;
    if (!owner) continue;
    const state = {
      handle: rec.handle,
      name:
        firstGroup(rec.groups, 2)?.trim() ??
        firstGroup(rec.groups, 300)?.trim() ??
        `state_${rec.handle}`,
    };
    const list = byOwner.get(owner);
    if (list) list.push(state);
    else byOwner.set(owner, [state]);
  }
  return byOwner;
}

interface PropertiesTableInfo {
  choices: string[];
  current: string;
}

function collectPropertiesTables(
  records: readonly RawRecord[],
): {
  byHandle: Map<string, PropertiesTableInfo>;
  byEvalGraph: Map<string, PropertiesTableInfo>;
} {
  // BLOCKPROPERTIESTABLE rows live in a flat listing of group 300
  // strings (the row "display name" column) + group 301 strings (the
  // key column used when the user picks a row). AutoCAD always emits
  // at least one "display name" per row. We do a lightweight pass
  // that collects every row's group-300 label; fidelity beyond that
  // belongs in the full Tier-2 parser (`dxfBridge.dynamic.parser.tier2`).
  const byHandle = new Map<string, PropertiesTableInfo>();
  // Also index by ownerHandle (the parent ACAD_EVALUATION_GRAPH) so a
  // lookup parameter with no direct table reference can still resolve
  // via the shared graph. AutoCAD 2018+ output uses this pattern.
  const byEvalGraph = new Map<string, PropertiesTableInfo>();
  for (const rec of records) {
    if (!isPropertiesTable(rec.type)) continue;
    const rowNames = collectGroups(rec.groups, 300)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    // Deduplicate while preserving order.
    const seen = new Set<string>();
    const choices: string[] = [];
    for (const r of rowNames) {
      if (!seen.has(r)) {
        seen.add(r);
        choices.push(r);
      }
    }
    // Group 91 is the "current row" index in the table (1-based). We
    // fall back to the first row when that group is absent or out of
    // range.
    const currentIndex = firstInt(rec.groups, 91);
    const current =
      typeof currentIndex === "number" &&
      currentIndex >= 1 &&
      currentIndex <= choices.length
        ? choices[currentIndex - 1]!
        : (choices[0] ?? "");
    const info: PropertiesTableInfo = { choices, current };
    byHandle.set(rec.handle, info);
    if (rec.ownerHandle) byEvalGraph.set(rec.ownerHandle, info);
  }
  return { byHandle, byEvalGraph };
}

// ═══════════════════════════════════════════════════════════════════════════
// Block → eval-graph membership
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a `blockName → eval-graph handle` map by walking the
 * DICTIONARIES produced in the OBJECTS section. Dynamic blocks own a
 * `DICTIONARY` (the BLOCK_RECORD's extension dictionary) whose entries
 * include `ACAD_ENHANCEDBLOCK` pointing at the `ACAD_EVALUATION_GRAPH`
 * (or `ACDBEVALGRAPH`) record that anchors all parameters. Returns an
 * empty map when the dictionary table is unavailable.
 */
export function buildBlockToEvalGraph(
  tablesSection: DxfTablesSection | null,
  objectsSection: DxfObjectsSection | null,
): Map<string, string> {
  const out = new Map<string, string>();
  if (!tablesSection || !objectsSection) return out;
  const dictByHandle = new Map<string, (typeof objectsSection.dictionaries)[number]>();
  for (const d of objectsSection.dictionaries) dictByHandle.set(d.handle, d);
  for (const rec of tablesSection.blockRecords) {
    if (!rec.extensionDictionaryHandle || !rec.name) continue;
    const dict = dictByHandle.get(rec.extensionDictionaryHandle);
    if (!dict) continue;
    const ent =
      dict.entries.find((e) => e.name === "ACAD_ENHANCEDBLOCK") ??
      dict.entries.find((e) => e.name === "AcDbBlockRepresentation");
    if (!ent) continue;
    out.set(rec.name, ent.handle);
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Stage A — extract per-block parameter definitions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Produce a `blockName → ParamDef[]` map covering every dynamic master
 * block in the drawing. The map is intentionally thin: the caller (stage B)
 * only needs name / type / default to seed `customProperties`. Blocks
 * without decodable parameters are absent from the map rather than
 * mapping to an empty array, so stage B can fast-exit on lookup miss.
 */
export function extractDynamicBlockParams(
  document: DxfDocument,
  blocks: Readonly<Record<string, BlockDefinition>>,
): Map<string, ParamDef[]> {
  const objectsSection = getObjectsSection(document);
  const tablesSection = getTablesSection(document);
  return extractDynamicBlockParamsFromSections(
    tablesSection,
    objectsSection,
    blocks,
  );
}

/**
 * Section-level variant used by unit tests — mirrors the split in
 * `dxfBridge.dynamic.detect.ts`. Pure over sections + blocks.
 */
export function extractDynamicBlockParamsFromSections(
  tablesSection: DxfTablesSection | null,
  objectsSection: DxfObjectsSection | null,
  blocks: Readonly<Record<string, BlockDefinition>>,
): Map<string, ParamDef[]> {
  const out = new Map<string, ParamDef[]>();

  // First pass — pull whatever the DYN-1 parser already decoded into
  // `block.dynamicMetadata.parameters`. This is the canonical path for
  // ezdxf / round-tripped fixtures that use `ACDBBLOCK*` class names.
  for (const [name, block] of Object.entries(blocks)) {
    const meta = block.dynamicMetadata;
    if (!meta || meta.parameters.length === 0) continue;
    const params: ParamDef[] = [];
    // DYN-4 Phase 3 Fix 6 — resolve the block's BlockPropertiesTable (when
    // constraint-based form is present) so user + constraint parameters
    // can discover their driver/readonly state. Legacy BTABLE-only tables
    // (entries[] populated, columns[] empty) don't participate in this
    // resolution — they stay on the existing lookupParameter path below.
    const propertiesTable = meta.blockPropertiesTable;
    const hasStructuredTable =
      propertiesTable !== undefined &&
      (propertiesTable.columns?.length ?? 0) > 0 &&
      (propertiesTable.rows?.length ?? 0) > 0;
    for (const p of meta.parameters) {
      switch (p.kind) {
        case "linear": {
          params.push({
            name: p.name,
            type: "distance",
            defaultValue: Math.abs(p.currentDistance ?? p.defaultDistance ?? 0),
          });
          break;
        }
        case "rotation": {
          const angle = p.currentAngle ?? 0;
          // The DYN-1 parser keeps rotation angles in degrees already
          // (see `dxfBridge.dynamic.parser.tier2.ts`).
          params.push({
            name: p.name,
            type: "angle",
            defaultValue: roundAngle(angle),
          });
          break;
        }
        case "flip": {
          params.push({
            name: p.name,
            type: "flip",
            defaultValue: p.currentValue ?? false,
          });
          break;
        }
        case "visibility": {
          const choices = p.states.map((s) => s.name);
          const current =
            p.states.find((s) => s.id === p.currentStateId) ??
            p.states.find((s) => s.id === p.defaultStateId) ??
            p.states[0];
          const def: ParamDef = {
            name: p.name,
            type: "visibility",
            defaultValue: current?.name ?? choices[0] ?? "",
          };
          params.push(choices.length > 0 ? { ...def, choices } : def);
          break;
        }
        case "lookup": {
          // The DYN-1 LookupParameter carries row metadata inside the
          // drawing-model's separate `lookupTables` collection. Derive
          // choices from the linked table when present. Row ids are
          // stable handles, which is exactly what
          // applyInstanceParameters expects as `customProperties[name]`.
          const table = meta.lookupTables.find(
            (t) => t.id === p.lookupTableId,
          );
          const choices = table?.rows.map((r) => r.id) ?? [];
          const current =
            p.currentLookupRowId ?? table?.rows[0]?.id ?? choices[0] ?? "";
          const def: ParamDef = {
            name: p.name,
            type: "lookup",
            defaultValue: current,
          };
          params.push(choices.length > 0 ? { ...def, choices } : def);
          break;
        }
        case "user": {
          // DYN-4 Phase 3 Fix 6 — user parameter (driven by an
          // ACDBASSOCVARIABLE). When the block has a constraint-based
          // BlockPropertiesTable whose key column references THIS user
          // parameter, emit as a `lookup`-type ParamDef whose choices are
          // the table's row keys (AutoCAD Properties palette dropdown).
          // Otherwise fall back to a typed readonly scalar (Площадь /
          // Масса display-only outputs on the ibeam block).
          const isKeyDriver =
            hasStructuredTable &&
            propertiesTable!.keyColumn !== undefined &&
            (propertiesTable!.keyColumn.driverParameterHandle === p.id ||
              propertiesTable!.keyColumn.driverParameterHandle ===
                (p.associatedVariableHandle ?? ""));
          if (isKeyDriver && propertiesTable) {
            const choices = (propertiesTable.rows ?? [])
              .map((r) => r.key)
              .filter((k): k is string => typeof k === "string" && k.length > 0);
            // Default to the associated variable's current value (string),
            // or the first authored row key when the association wasn't
            // resolvable.
            const stringifiedDefault =
              typeof p.defaultValue === "string" && p.defaultValue.length > 0
                ? p.defaultValue
                : typeof p.defaultValue === "number"
                  ? String(p.defaultValue)
                  : "";
            const current =
              stringifiedDefault.length > 0 && choices.includes(stringifiedDefault)
                ? stringifiedDefault
                : (choices[0] ?? "");
            const def: ParamDef = {
              name: p.variableName || p.name,
              type: "lookup",
              defaultValue: current,
            };
            params.push(choices.length > 0 ? { ...def, choices } : def);
          } else {
            // Standalone user param — readonly display. Type follows the
            // ACDBASSOCVARIABLE's declared value type (`area` surfaces as
            // its own ParamDef type so the Inspector can render the m²
            // suffix; everything else falls back to `distance`).
            const paramType: ParamDef["type"] =
              p.valueType === "area"
                ? "area"
                : p.valueType === "angle"
                  ? "angle"
                  : "distance";
            params.push({
              name: p.variableName || p.name,
              type: paramType,
              defaultValue: p.defaultValue ?? 0,
              readonly: true,
            });
          }
          break;
        }
        case "constraint-horizontal":
        case "constraint-vertical":
        case "constraint-aligned": {
          // Constraint distance drivers. When the block has a
          // constraint-based BlockPropertiesTable whose columns reference
          // THIS constraint's handle via an `updated-distance` link, the
          // value is driven by the selected row — mark `readonly`.
          const driven =
            hasStructuredTable &&
            isConstraintDrivenByTable(p.id, propertiesTable!, "updated-distance");
          const def: ParamDef = {
            name: p.name,
            type: "distance",
            defaultValue: Math.abs(p.currentDistance ?? p.defaultDistance ?? 0),
            ...(driven ? { readonly: true } : {}),
          };
          params.push(def);
          break;
        }
        case "constraint-radial": {
          const driven =
            hasStructuredTable &&
            isConstraintDrivenByTable(p.id, propertiesTable!, "updated-distance");
          const def: ParamDef = {
            name: p.name,
            type: "distance",
            defaultValue: Math.abs(p.currentRadius ?? p.defaultRadius ?? 0),
            ...(driven ? { readonly: true } : {}),
          };
          params.push(def);
          break;
        }
        case "constraint-diameter": {
          const driven =
            hasStructuredTable &&
            isConstraintDrivenByTable(p.id, propertiesTable!, "updated-distance");
          const def: ParamDef = {
            name: p.name,
            type: "distance",
            defaultValue: Math.abs(
              p.currentDiameter ?? p.defaultDiameter ?? 0,
            ),
            ...(driven ? { readonly: true } : {}),
          };
          params.push(def);
          break;
        }
        case "constraint-angular": {
          const driven =
            hasStructuredTable &&
            isConstraintDrivenByTable(p.id, propertiesTable!, "updated-angle");
          // Angular constraints are stored in radians on АБИ1.dxf
          // (parser.constraints.ts preserves the raw value); surface to
          // the Inspector as degrees so it matches AutoCAD's Properties
          // palette convention.
          const rawAngle = p.currentAngle ?? p.defaultAngle ?? 0;
          // Heuristic: values within [-2π, 2π] that are NOT integer-degree
          // are treated as radians (the real-file encoding); larger values
          // are treated as degrees (synthetic writer encoding where the
          // round-trip test asserts degrees directly). This keeps the
          // existing Batch-C tests green while the АБИ1 path converts to
          // degrees.
          const looksLikeRadians =
            Math.abs(rawAngle) <= 2 * Math.PI + 1e-9 &&
            !Number.isInteger(rawAngle);
          const displayDeg = looksLikeRadians
            ? (rawAngle * 180) / Math.PI
            : rawAngle;
          const def: ParamDef = {
            name: p.name,
            type: "angle",
            defaultValue: roundAngle(displayDeg),
            ...(driven ? { readonly: true } : {}),
          };
          params.push(def);
          break;
        }
        // XY / Point / Alignment are out of scope for this pass — see the
        // file header's "Scope" note. They carry multi-component values
        // (x, y) that map to suffixed customProperties keys; a later
        // TODO(complex-params) pass can populate them.
        default:
          break;
      }
    }
    if (params.length > 0) out.set(name, params);
  }

  // Second pass — raw-groups scan for the un-prefixed AutoCAD classes.
  // This covers real-world AutoCAD 2018+ output where the DYN-1 parser
  // saw nothing (the prefix-less class names fall through
  // isSupportedDynamicBlockType).
  if (objectsSection) {
    const records = splitRawRecords(objectsSection.rawObjectGroups);
    // Skip the raw pass when every candidate block already has typed
    // parameters — saves work on pure-ezdxf fixtures.
    const blockToGraph = buildBlockToEvalGraph(tablesSection, objectsSection);
    const statesByOwner = collectVisibilityStates(records);
    const { byHandle: tablesByHandle, byEvalGraph: tablesByEvalGraph } =
      collectPropertiesTables(records);

    // Group records by ownerHandle for O(1) lookup per block.
    const recordsByOwner = new Map<string, RawRecord[]>();
    for (const rec of records) {
      if (!rec.ownerHandle) continue;
      const list = recordsByOwner.get(rec.ownerHandle);
      if (list) list.push(rec);
      else recordsByOwner.set(rec.ownerHandle, [rec]);
    }

    for (const [blockName, block] of Object.entries(blocks)) {
      if (out.has(blockName)) continue; // already covered by DYN-1 parser
      if (block.dynamicBlock !== true) continue;
      const evalHandle = blockToGraph.get(blockName);
      if (!evalHandle) continue;
      const evalChildren = recordsByOwner.get(evalHandle) ?? [];
      const params: ParamDef[] = [];
      for (const rec of evalChildren) {
        try {
          if (isLinearParameter(rec.type)) {
            params.push(decodeLinear(rec));
          } else if (isRotationParameter(rec.type)) {
            params.push(decodeRotation(rec));
          } else if (isFlipParameter(rec.type)) {
            params.push(decodeFlip(rec));
          } else if (isVisibilityParameter(rec.type)) {
            params.push(decodeVisibility(rec, statesByOwner));
          } else if (isLookupParameter(rec.type)) {
            params.push(decodeLookup(rec, tablesByHandle, tablesByEvalGraph));
          }
          // TODO(complex-params): BLOCKXYPARAMETER / BLOCKPOINTPARAMETER /
          // BLOCKALIGNMENTPARAMETER / BLOCK*CONSTRAINTPARAMETER carry
          // multi-component values. Leaving them for a follow-up pass.
        } catch (err) {
          // Graceful skip — one malformed parameter record must not
          // bring down the whole block. Log once per record.
          if (typeof console !== "undefined") {
            console.warn(
              `[dxfBridge.dynamic.params] skipped malformed ${rec.type} ${rec.handle}:`,
              err,
            );
          }
        }
      }
      if (params.length > 0) out.set(blockName, params);
    }
  }

  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Stage B — attach defaults to every INSERT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Resolve anonymous dynamic-block variant names (`*U<N>`) to their
 * master name by reading the `AcDbBlockRepBTag` XData handle (group
 * 1005 → master BLOCK_RECORD handle) or `AcDbDynamicBlockTrueName` /
 * `AcDbDynamicBlockTrueName2` XData string (group 1000). Returns a
 * (variantName → masterName) map; named masters map to themselves so
 * callers can blindly pass any block name through.
 */
function buildVariantToMasterMap(
  tablesSection: DxfTablesSection | null,
): Map<string, string> {
  const out = new Map<string, string>();
  if (!tablesSection) return out;
  const byHandle = new Map<string, string>();
  for (const rec of tablesSection.blockRecords) {
    if (rec.handle && rec.name) byHandle.set(rec.handle, rec.name);
  }
  for (const rec of tablesSection.blockRecords) {
    if (!rec.name) continue;
    out.set(rec.name, rec.name); // identity default
    if (!rec.xdata) continue;
    // AcDbBlockRepBTag group 1005 = master handle.
    const btag = rec.xdata.find(
      (x: DxfXDataEntry) => x.appName === "AcDbBlockRepBTag",
    );
    if (btag) {
      const handle = btag.groups.find((g) => g.code === 1005)?.value.trim();
      if (handle) {
        const master = byHandle.get(handle);
        if (master && master !== rec.name) {
          out.set(rec.name, master);
          continue;
        }
      }
    }
    // AcDbDynamicBlockTrueName / TrueName2 group 1000 = master name.
    const tn = rec.xdata.find(
      (x: DxfXDataEntry) =>
        x.appName === "AcDbDynamicBlockTrueName" ||
        x.appName === "AcDbDynamicBlockTrueName2",
    );
    if (tn) {
      const name = tn.groups.find((g) => g.code === 1000)?.value.trim();
      if (name && name !== rec.name) out.set(rec.name, name);
    }
  }
  return out;
}

/**
 * Shape-level view of what `attachInsertCustomProperties` needs from a
 * drawing-model space. Extracted as a minimal structural interface so
 * the call site can pass an array literal without tripping
 * exactOptionalPropertyTypes on the full DrawingSpace schema (many
 * optional fields like `refeditWorkingSet` are not required here).
 */
export interface AttachSpacesLike {
  readonly entities: DrawingEntity[];
}

/**
 * Mutate every Block INSERT in the given spaces so its
 * `customProperties` carries the master block's authored parameter
 * defaults. Blocks with no paramDefinitions entry are left unchanged
 * (keeping `customProperties: {}` for static blocks).
 *
 * @param document         parsed DXF (source of variant → master xdata
 *                         resolution)
 * @param spaces           drawing-model spaces whose entities are the
 *                         freshly-imported INSERTs
 * @param paramDefinitions output of `extractDynamicBlockParams`
 */
export function attachInsertCustomProperties(
  document: DxfDocument,
  spaces: readonly AttachSpacesLike[],
  paramDefinitions: ReadonlyMap<string, ParamDef[]>,
): void {
  if (paramDefinitions.size === 0) return;
  const variantToMaster = buildVariantToMasterMap(getTablesSection(document));

  for (const space of spaces) {
    for (const entity of space.entities) {
      if (entity.type !== "Block") continue;
      const blockName = entity.blockName;
      const masterName = variantToMaster.get(blockName) ?? blockName;
      const params =
        paramDefinitions.get(masterName) ?? paramDefinitions.get(blockName);
      if (!params || params.length === 0) continue;
      // Respect anything the caller has already placed in
      // customProperties (e.g. evaluator-seeded runtime overrides) —
      // only fill KEYS that are absent. This keeps the function
      // idempotent.
      const existing = entity.customProperties ?? {};
      let mutated = false;
      for (const p of params) {
        if (Object.prototype.hasOwnProperty.call(existing, p.name)) continue;
        (existing as Record<string, string | number | boolean>)[p.name] =
          p.defaultValue;
        mutated = true;
      }
      if (mutated) {
        // `entity.customProperties` is already an object reference we
        // have mutated above; reassign defensively in case the caller
        // started with `undefined`.
        (entity as { customProperties: Record<string, string | number | boolean> }).customProperties = existing;
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Test hooks
// ═══════════════════════════════════════════════════════════════════════════

/** Internal helpers re-exported for unit tests only. */
export const __testables = {
  splitRawRecords,
  collectVisibilityStates,
  collectPropertiesTables,
  buildBlockToEvalGraph,
  buildVariantToMasterMap,
  decodeLinear,
  decodeRotation,
  decodeFlip,
  decodeVisibility,
  decodeLookup,
};

// Silence unused-type-import lint where the parser does not reference
// the symbol directly.
export type { DxfDynamicBlockObject };
